'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import { News } from '@/types';
import { useTranslation } from '@/context/LanguageContext';
import { NewsItem } from '@/types/news';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';

interface NewsCardProps {
  news: News;
  basePath?: string; // Optional: defaults to '/berita'
  raw?: NewsItem; // Optional: raw API data for dual language support
}

export default function NewsCard({ news, basePath = '/berita', raw }: NewsCardProps) {
  const { language, t } = useTranslation();
  const detailUrl = `${basePath}/${news.slug}`;

  // Use raw data if available for dynamic language switching
  const title = raw ? (language === 'en' ? (raw.title_en || raw.title_idn) : (raw.title_idn || raw.title_en)) : news.title;

  // Generate excerpt from raw content if available
  const content = raw ? (language === 'en' ? (raw.content_en || raw.content_idn) : (raw.content_idn || raw.content_en)) : '';
  const excerpt = raw ? (content.replace(/<[^>]+>/g, '').substring(0, 150) + '...') : news.excerpt;

  // Determine initial image source
  let initialImageSrc = news.image;
  if (raw && raw.filename) {
    initialImageSrc = `https://unmul.ac.id/uploads/posts/${raw.filename}`;
  }

  const [currentSrc, setCurrentSrc] = useState(initialImageSrc);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setCurrentSrc(initialImageSrc);
  }, [initialImageSrc]);

  return (
    <article className="flex flex-col h-full bg-white rounded-xl shadow-sm hover:shadow-xl transition border border-gray-100 group" data-aos="fade-up">
      <div className="h-48 overflow-hidden rounded-t-xl relative bg-gray-100">
        <span className={`absolute top-4 left-4 ${news.categoryColor} text-xs font-bold px-3 py-1 rounded z-10`}>
          {news.category}
        </span>
        <img
          src={currentSrc}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (!imgError) {
              setImgError(true);
              if (raw && raw.id && !currentSrc.includes('doServicesProxy')) {
                // If using state, we should update state, but for img tag we can just update src directly to avoid re-render loops if caution
                // But since we use state for src...
                // Actually with img tag we can just manipulate DOM src safely for fallback or use state.
                // Let's stick to the previous robust logical:
                // Wait, if I use state 'currentSrc', I should update state.
                setCurrentSrc(`/api/doServicesProxy?endpoint=web/file/news/${raw.id}&isImage=true`);
              } else {
                setCurrentSrc('https://via.placeholder.com/600x400/006633/FFCC00?text=UNMUL+News');
              }
            }
          }}
        />
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="text-xs text-gray-500 mb-2 font-medium">
          <FontAwesomeIcon icon={faCalendar} className="mr-2" />{news.date}
        </div>
        <h3 className="text-lg font-bold text-gray-800 mb-3 group-hover:text-unmulGreen transition line-clamp-2">
          <Link href={detailUrl} className="focus:outline-none focus:underline decoration-unmulGreen decoration-2">
            {title}
          </Link>
        </h3>
        <p className="text-gray-600 text-sm line-clamp-3 mb-4">{excerpt}</p>
        <div className="mt-auto">
          <Link
            href={detailUrl}
            className="text-unmulGreen font-bold text-sm hover:underline inline-flex items-center gap-1 min-h-[44px] min-w-[44px]"
            aria-label={`${t('common.readMore')} - ${title}`}
          >
            {t('common.readMore')}
            <FontAwesomeIcon icon={faArrowRight} className="text-xs group-hover:translate-x-1 transition" />
          </Link>
        </div>
      </div>
    </article>
  );
}
